import React, { useState, useEffect, useMemo } from 'react';
import { 
    Bell, Plus, Trash2, Send, Sparkles, Wand2, 
    Zap, Info, CheckCircle, AlertTriangle, Loader2,
    Target, Globe, ShieldCheck, X, RefreshCw, Layers,
    Activity, ShieldAlert, Microscope, Database, ChevronRight,
    Cpu, ActivitySquare, Terminal, User
} from 'lucide-react';
import { NotificationService, NotificationItem } from '../../services/notificationService';
import { StorageService } from '../../services/storageService';
import { CertificationType } from '../../types';

interface AuditableNotification extends NotificationItem {
    isSystemGenerated: boolean;
    triggerContext?: string;
}

export const NotificationManager: React.FC = () => {
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [history, setHistory] = useState<AuditableNotification[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);
    const [activeTab, setActiveTab] = useState<'broadcasts' | 'automation'>('broadcasts');
    
    const [form, setForm] = useState<Partial<NotificationItem>>({
        title: '', message: '', type: 'info', targetTier: 'all', certId: ''
    });

    const [aiContext, setAiContext] = useState('');
    const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                const [c, h] = await Promise.all([
                    StorageService.getCertifications(),
                    NotificationService.adminGetAll().catch(err => {
                        console.error("Broadcast Hydration Failure:", err);
                        return [];
                    })
                ]);
                setCerts(c);
                setHistory(Array.isArray(h) ? h as AuditableNotification[] : []);
                if (c.length > 0) setForm(prev => ({ ...prev, certId: c[0].id }));
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, []);

    const filteredHistory = useMemo(() => {
        // Defensive check to prevent crash if history is not an array
        const allHistory = Array.isArray(history) ? history : [];
        if (activeTab === 'automation') return allHistory.filter(h => h.isSystemGenerated);
        return allHistory.filter(h => !h.isSystemGenerated);
    }, [history, activeTab]);

    const automationStats = useMemo(() => {
        // Defensive check to prevent crash if history is not an array
        const allHistory = Array.isArray(history) ? history : [];
        const system = allHistory.filter(h => h.isSystemGenerated);
        return {
            total: system.length,
            inactivity: system.filter(h => h.title?.includes('Welcome')).length,
            remediation: system.filter(h => h.type === 'remediation').length,
            pro: system.filter(h => h.type === 'pro').length
        };
    }, [history]);

    const handleSend = async () => {
        if (!form.title || !form.message) return;
        setIsSaving(true);
        setStatus(null);
        try {
            await NotificationService.adminCreate(form);
            setStatus({ type: 'success', text: 'Broadcast protocol committed to registry.' });
            setForm({ ...form, title: '', message: '' });
            const latest = await NotificationService.adminGetAll();
            setHistory(Array.isArray(latest) ? latest as AuditableNotification[] : []);
        } catch (e) {
            setStatus({ type: 'error', text: 'Registry sync failed.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleAiSuggest = async () => {
        if (!aiContext.trim()) return;
        setIsGenerating(true);
        try {
            const data = await NotificationService.adminSuggestAI(aiContext);
            setAiSuggestions(Array.isArray(data) ? data : []);
        } catch (e) {
            setStatus({ type: 'error', text: 'AI logic bridge error.' });
        } finally {
            setIsGenerating(false);
        }
    };

    const useSuggestion = (s: any) => {
        setForm({ ...form, title: s.title, message: s.message, type: s.type });
        setAiSuggestions([]);
        setAiContext('');
    };

    return (
        <div className="space-y-8 animate-fade-in pb-12 text-slate-900 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-950 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/10">
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><Bell size={180}/></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl"><Activity size={24}/></div>
                        <h2 className="text-3xl font-black tracking-tight">Broadcast Center</h2>
                    </div>
                    <p className="text-slate-400 font-medium text-sm md:text-lg max-w-2xl">Provision manual alerts or audit system-generated Behavioral Triggers.</p>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-2xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertTriangle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
                <div className="xl:col-span-7 space-y-8">
                    <div className="bg-white rounded-[3rem] p-10 border border-slate-200 shadow-sm space-y-8">
                        <div className="flex items-center gap-4 border-b border-slate-100 pb-8">
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl"><Wand2 size={24}/></div>
                            <h3 className="text-xl font-black tracking-tight">Alert Architect</h3>
                        </div>

                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Target Jurisdiction</label>
                                    <select 
                                        value={form.certId} 
                                        onChange={e => setForm({...form, certId: e.target.value})}
                                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-[11px] outline-none focus:bg-white"
                                    >
                                        <option value="">All Certifications</option>
                                        {certs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Tier Priority</label>
                                    <select 
                                        value={form.targetTier} 
                                        onChange={e => setForm({...form, targetTier: e.target.value as any})}
                                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-[11px] outline-none focus:bg-white"
                                    >
                                        <option value="all">All Users</option>
                                        <option value="free">Free Only</option>
                                        <option value="paid">Elite Pro Only</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Alert Category</label>
                                <div className="flex flex-wrap gap-2">
                                    {['info', 'success', 'warning', 'pro', 'remediation'].map(t => (
                                        <button 
                                            key={t}
                                            onClick={() => setForm({...form, type: t as any})}
                                            className={`px-6 py-2.5 rounded-xl text-[9px] font-black tracking-widest border-2 transition-all ${form.type === t ? 'bg-slate-900 text-white border-slate-900 shadow-md' : 'bg-white border-slate-100 text-slate-400'}`}
                                        >{t}</button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Headline Content</label>
                                <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full p-5 bg-slate-50 border border-slate-200 rounded-2xl font-black text-lg outline-none focus:bg-white shadow-inner tracking-tight" placeholder="e.g., Clinical Registry Update" />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Narrative Message</label>
                                <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full p-6 bg-slate-50 border border-slate-200 rounded-[2rem] font-medium text-slate-700 outline-none focus:bg-white h-32 resize-none shadow-inner" placeholder="Detailed alert instruction..." />
                            </div>

                            <button 
                                onClick={handleSend}
                                disabled={isSaving || !form.title || !form.message}
                                className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-widest shadow-2xl hover:bg-black transition-all border-b-4 border-black flex items-center justify-center gap-3 disabled:opacity-50"
                            >
                                {isSaving ? <Loader2 className="animate-spin" /> : <Send size={18}/>} Commit & Broadcast
                            </button>
                        </div>
                    </div>

                    <div className="bg-indigo-600 rounded-[3rem] p-10 text-white shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:rotate-12 transition-transform duration-1000"><Sparkles size={120}/></div>
                        <div className="relative z-10 space-y-6">
                            <h4 className="text-xl font-black tracking-tight flex items-center gap-3">
                                <Zap className="text-amber-300" size={24}/> AI Notification Synthesis
                            </h4>
                            <div className="flex flex-col md:flex-row gap-4">
                                <input 
                                    type="text" 
                                    value={aiContext}
                                    onChange={e => setAiContext(e.target.value)}
                                    placeholder="e.g., Encourage students to clear their Mistake Vaults..."
                                    className="flex-1 p-5 bg-white/10 border border-white/20 rounded-2xl outline-none font-bold text-sm text-white placeholder:text-indigo-300 focus:bg-white/20"
                                />
                                <button 
                                    onClick={handleAiSuggest}
                                    disabled={isGenerating || !aiContext.trim()}
                                    className="px-10 py-5 bg-white text-indigo-600 rounded-2xl font-black text-[10px] tracking-widest shadow-2xl hover:bg-indigo-50 transition-all disabled:opacity-50"
                                >
                                    {isGenerating ? <Loader2 className="animate-spin" /> : 'Suggest Alerts'}
                                </button>
                            </div>

                            {aiSuggestions.length > 0 && (
                                <div className="grid grid-cols-1 gap-3 animate-slide-up pt-4">
                                    {aiSuggestions.map((s, i) => (
                                        <div key={i} className="p-5 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-between group hover:bg-white hover:text-indigo-900 transition-all cursor-pointer" onClick={() => useSuggestion(s)}>
                                            <div className="min-w-0">
                                                <p className="font-black text-xs tracking-tight">{s.title}</p>
                                                <p className="text-[10px] font-medium opacity-60 line-clamp-1">"{s.message}"</p>
                                            </div>
                                            <ChevronRight size={18} className="shrink-0 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="xl:col-span-5 flex flex-col min-h-0 bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-2 bg-slate-100 flex gap-1">
                        <button 
                            onClick={() => setActiveTab('broadcasts')}
                            className={`flex-1 py-3 rounded-2xl text-[9px] font-black tracking-widest transition-all ${activeTab === 'broadcasts' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}
                        >Manual History</button>
                        <button 
                            onClick={() => setActiveTab('automation')}
                            className={`flex-1 py-3 rounded-2xl text-[9px] font-black tracking-widest transition-all ${activeTab === 'automation' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}
                        >Automated Pulse</button>
                    </div>

                    {activeTab === 'automation' && (
                        <div className="p-4 grid grid-cols-2 gap-2 border-b border-slate-100 bg-slate-50/30">
                            <div className="p-3 bg-white rounded-xl border border-slate-100 flex justify-between items-center">
                                <span className="text-[8px] font-black text-slate-400">Inactivity</span>
                                <span className="text-xs font-black text-slate-900">{automationStats.inactivity}</span>
                            </div>
                            <div className="p-3 bg-white rounded-xl border border-slate-100 flex justify-between items-center">
                                <span className="text-[8px] font-black text-slate-400">Remediation</span>
                                <span className="text-xs font-black text-rose-600">{automationStats.remediation}</span>
                            </div>
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-3 bg-slate-50/30">
                        {isLoading ? (
                            <div className="p-12 text-center opacity-30 animate-pulse"><Loader2 className="animate-spin mx-auto" /></div>
                        ) : filteredHistory.length === 0 ? (
                            <div className="p-12 text-center opacity-20">
                                {activeTab === 'automation' ? <Cpu size={48} className="mx-auto mb-4" /> : <Database size={48} className="mx-auto mb-4" />}
                                <p className="font-black text-[10px]">Registry Empty</p>
                            </div>
                        ) : (
                            filteredHistory.map(item => (
                                <div key={item._id} className={`p-5 bg-white rounded-[2rem] border-2 transition-all ${activeTab === 'automation' ? 'border-indigo-100 hover:border-indigo-400' : 'border-slate-200 hover:border-indigo-400'}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded-lg text-[7px] font-black ${item.isSystemGenerated ? 'bg-indigo-600 text-white' : 'bg-slate-900 text-white'}`}>
                                                {item.isSystemGenerated ? 'LOGIC TRIGGER' : 'ADMIN PUSH'}
                                            </span>
                                            <span className="text-[7px] font-black text-slate-300">{new Date(item.createdAt).toLocaleString()}</span>
                                        </div>
                                        {!item.isSystemGenerated && (
                                            <button 
                                                onClick={() => setForm(item)}
                                                className="text-[8px] font-black text-indigo-500 hover:underline"
                                            >Reuse</button>
                                        )}
                                    </div>
                                    <h4 className="font-black text-xs tracking-tight text-slate-800">{item.title}</h4>
                                    <p className="text-[10px] text-slate-500 font-medium leading-relaxed mt-1 line-clamp-2">"{item.message}"</p>
                                    
                                    <div className="mt-4 pt-3 border-t border-slate-50 space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[7px] font-black text-slate-400">Target: {item.targetTier}</span>
                                            {item.certId && <span className="text-[7px] font-black text-slate-400">Cert: {item.certId}</span>}
                                        </div>
                                        {item.isSystemGenerated && item.triggerContext && (
                                            <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 rounded-lg">
                                                <User size={10} className="text-indigo-400" />
                                                <span className="text-[8px] font-mono text-indigo-600 truncate">{item.triggerContext}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};